import React from "react";
import { useParams, Link } from "react-router-dom";

import { LeaguePageNav } from "../components/league/LeaguePageNav";
import { Spinner } from "../components/common/Spinner";
import PlayerCardMini from "../components/cards/PlayerCardMini";
import { useActiveLeague } from "../hooks/useActiveLeague";
import { useAuth } from "../context/AuthContext";
import { useGetLeagueBySlug } from "../api/leaguesApi.js";
import { useGetTeamOfTheWeek } from "../api/totwApi.js";
import { useGetTeams } from "../api/teamsApi.js";
import { shareOrCopy } from "../utils/share.js";

// Rows render attackers first; reveal runs bottom-up (GK first).
const ROW_BUCKETS = ["FWD", "MID", "DEF", "GK"];
const OWNER_ACCENT_COUNT = 6;
const REVEAL_LEAD_MS = 200;
const REVEAL_STEP_MS = 260;
const REVEAL_TAIL_MS = 300;

const idOf = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  return String(value._id ?? value.$oid ?? value.id ?? value);
};

function groupRows(team) {
  const byBucket = new Map(ROW_BUCKETS.map((bucket) => [bucket, []]));
  team.forEach((entry) => {
    const bucket = ROW_BUCKETS.includes(entry.bucket) ? entry.bucket : "MID";
    byBucket.get(bucket).push(entry);
  });
  return ROW_BUCKETS
    .map((bucket) => ({ bucket, entries: byBucket.get(bucket) }))
    .filter((row) => row.entries.length > 0);
}

// Stable accent per team: index of first appearance in the XI, cycled.
function buildOwnerAccents(team) {
  const accents = new Map();
  team.forEach((entry) => {
    const key = idOf(entry.teamId) ?? entry.teamName;
    if (key && !accents.has(key)) {
      accents.set(key, accents.size % OWNER_ACCENT_COUNT);
    }
  });
  return accents;
}

function TeamOfTheWeekPage() {
  const { slug } = useParams();
  const { activeLeague } = useActiveLeague();
  const { user } = useAuth();
  const { data: slugLeagueData, isLoading: isLoadingLeague } = useGetLeagueBySlug(slug);

  const rawLeague = slugLeagueData?.data;
  const league = rawLeague?.league ?? rawLeague;
  const leagueId = league?._id ?? activeLeague?._id ?? null;
  const leagueName = league?.name ?? activeLeague?.name ?? "my league";

  const [selectedRound, setSelectedRound] = React.useState(null);
  const [revealState, setRevealState] = React.useState("covered");
  const revealTimerRef = React.useRef(null);

  const { data, isLoading: isLoadingTotw } = useGetTeamOfTheWeek(leagueId, selectedRound);

  // The teams query is scoped to the active league, so every returned team is
  // the viewer's team in this league (same resolution as Layout.jsx).
  const { data: myTeamsResponse } = useGetTeams(
    !!user && !!(slug || leagueId),
    leagueId,
    slug ?? null
  );
  const myTeamIds = React.useMemo(() => {
    const teams = Array.isArray(myTeamsResponse)
      ? myTeamsResponse
      : myTeamsResponse ? [myTeamsResponse] : [];
    return new Set(teams.map((team) => idOf(team?._id)).filter(Boolean));
  }, [myTeamsResponse]);

  React.useEffect(() => () => clearTimeout(revealTimerRef.current), []);

  const team = React.useMemo(() => (Array.isArray(data?.team) ? data.team : []), [data]);
  const availableRounds = React.useMemo(
    () => (Array.isArray(data?.availableRounds) ? data.availableRounds : []),
    [data]
  );
  const rows = React.useMemo(() => groupRows(team), [team]);
  const ownerAccents = React.useMemo(() => buildOwnerAccents(team), [team]);
  const myCount = React.useMemo(
    () => team.filter((entry) => myTeamIds.has(idOf(entry.teamId))).length,
    [team, myTeamIds]
  );

  // Reveal delays count bottom-up: GK slot 0, then DEF, MID, FWD.
  const slotOrders = React.useMemo(() => {
    const orders = new Map();
    let order = 0;
    [...rows].reverse().forEach((row) => {
      row.entries.forEach((entry) => {
        orders.set(entry.wyId, order);
        order += 1;
      });
    });
    return orders;
  }, [rows]);

  const currentRound = selectedRound ?? data?.round ?? null;
  const roundIndex = currentRound != null ? availableRounds.indexOf(currentRound) : -1;
  const canPrev = roundIndex > 0;
  const canNext = roundIndex !== -1 && roundIndex < availableRounds.length - 1;

  const changeRound = (offset) => {
    const nextRound = availableRounds[roundIndex + offset];
    if (nextRound === undefined) return;
    // Round switches skip the cover replay; only the first view gets it.
    if (revealState !== "covered") {
      clearTimeout(revealTimerRef.current);
      setRevealState("done");
    }
    setSelectedRound(nextRound);
  };

  const handleReveal = () => {
    setRevealState("revealing");
    const total = REVEAL_LEAD_MS + team.length * REVEAL_STEP_MS + REVEAL_TAIL_MS;
    revealTimerRef.current = setTimeout(() => setRevealState("done"), total);
  };

  const handleShare = () => {
    const madeIt = myCount > 0 ? `, ${myCount} of my players made it` : "";
    shareOrCopy({
      title: "Team of the Week",
      text: `Round ${currentRound} Team of the Week in ${leagueName}${madeIt}`,
      url: `/${slug}/totw`,
    });
  };

  const isLoading = isLoadingLeague || (isLoadingTotw && !data);
  const isEmpty = !isLoading && (currentRound == null || availableRounds.length === 0 || team.length === 0);
  const showChip = revealState === "done" && myCount > 0;
  const isQuickplay = league?.gameMode === "quickplay";

  return (
    <div className="page-league page-totw">
      <div className="wrapper">
        <LeaguePageNav />

        <div className="title-block">
          <h1>{league?.name}</h1>
          <div className="ctrl-block">
            <Link to={"/switch-league"} className="read-more-btn">
              Switch league
              <span className="ico icon-arrow-right" />
            </Link>
          </div>
        </div>

        {isQuickplay ? (
          <section className="totw-panel">
            <p className="totw-empty">Team of the Week is only available in full leagues.</p>
          </section>
        ) : (
          <section className="totw-panel">
            <div className="totw-head">
              <div className="totw-head__text">
                <h3 className="totw-title">Team of the <em>Week</em></h3>
                <div className="totw-meta">
                  {!isEmpty && (
                    <span className="totw-round-picker">
                      <button
                        type="button"
                        className="totw-round-btn"
                        onClick={() => changeRound(-1)}
                        disabled={!canPrev}
                        aria-label="Previous round"
                      >
                        <span className="ico icon-arrow-left" />
                      </button>
                      <span className="totw-round-label">Round {currentRound}</span>
                      <button
                        type="button"
                        className="totw-round-btn"
                        onClick={() => changeRound(1)}
                        disabled={!canNext}
                        aria-label="Next round"
                      >
                        <span className="ico icon-arrow-right" />
                      </button>
                    </span>
                  )}
                  {!isEmpty && <span className="totw-meta__sep">|</span>}
                  <span>Picked from match ratings</span>
                </div>
              </div>
              {showChip && (
                <span className="totw-chip">{myCount} of your players made it</span>
              )}
              {!isEmpty && (
                <button type="button" className="gold-cta gold-cta--ghost" onClick={handleShare}>
                  Share TOTW
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="loader">
                <Spinner />
              </div>
            ) : isEmpty ? (
              <p className="totw-empty">
                Team of the Week drops when a full round of matches has been played.
              </p>
            ) : (
              <div className="totw-pitch">
                {revealState === "covered" ? (
                  <div className="totw-cover">
                    <span className="totw-cover__tag">Round {currentRound} ratings are in</span>
                    <button type="button" className="gold-cta" onClick={handleReveal}>
                      Reveal Team of the Week
                    </button>
                  </div>
                ) : (
                  rows.map((row) => (
                    <div className="totw-row" key={row.bucket} data-bucket={row.bucket}>
                      {row.entries.map((entry) => {
                        const accent = ownerAccents.get(idOf(entry.teamId) ?? entry.teamName) ?? 0;
                        const order = slotOrders.get(entry.wyId) ?? 0;
                        const slotClass = revealState === "revealing"
                          ? "totw-slot totw-slot--in"
                          : "totw-slot totw-slot--shown";
                        return (
                          <div
                            className={slotClass}
                            key={entry.wyId}
                            style={revealState === "revealing"
                              ? { "--totw-delay": `${REVEAL_LEAD_MS + order * REVEAL_STEP_MS}ms` }
                              : undefined}
                          >
                            <PlayerCardMini
                              player={{
                                wyId: entry.wyId,
                                name: entry.name,
                                position: entry.position,
                                overall: entry.overall,
                                averageRating: entry.rating,
                              }}
                            />
                            <span className={`totw-own totw-own--${accent}`}>{entry.teamName}</span>
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export { TeamOfTheWeekPage };
